import {
  NetworkArbitrumOne,
  NetworkAvalanche,
  NetworkBase,
  NetworkBinanceSmartChain,
  NetworkEthereum,
  NetworkOptimism,
  NetworkPolygon,
  NetworkSolana,
  NetworkTron,
  TokenAVAX,
  TokenBNB,
  TokenBTC,
  TokenDAI,
  TokenETH,
  TokenPOL,
  TokenSOL,
  TokenTRX,
  TokenUSDC,
  TokenUSDT,
  TokenWBTC,
} from "@web3icons/react";
import type { ComponentType } from "react";
import { cx } from "./primitives";

/**
 * Token and network logos (web3icons, MIT). Tokens are round, networks are rounded squares, so
 * the two never read as each other. Every symbol of the registry (packages/shared) has a logo:
 * wrapped and bridged tokens show the asset they stand for (WETH → ETH, USDC.e → USDC). Anything
 * unknown falls back to a neutral badge with its initials. The logos are decorative: the symbol
 * or network name is always shown as text beside them.
 */

type Logo = ComponentType<{ variant?: "branded" | "mono" | "background"; size?: number | string }>;

const TOKEN_LOGOS: Record<string, Logo> = {
  USDC: TokenUSDC,
  "USDC.E": TokenUSDC,
  USDBC: TokenUSDC,
  USDT: TokenUSDT,
  "USDT.E": TokenUSDT,
  USDT0: TokenUSDT,
  DAI: TokenDAI,
  ETH: TokenETH,
  WETH: TokenETH,
  "WETH.E": TokenETH,
  WBTC: TokenWBTC,
  "WBTC.E": TokenWBTC,
  CBBTC: TokenBTC,
  BTCB: TokenBTC,
  "BTC.B": TokenBTC,
  BTC: TokenBTC,
  BNB: TokenBNB,
  WBNB: TokenBNB,
  POL: TokenPOL,
  WPOL: TokenPOL,
  MATIC: TokenPOL,
  AVAX: TokenAVAX,
  WAVAX: TokenAVAX,
  SOL: TokenSOL,
  WSOL: TokenSOL,
  TRX: TokenTRX,
};

/** By the registry's network slug (the same for a mainnet and its testnet). */
const NETWORK_LOGOS: Record<string, Logo> = {
  ethereum: NetworkEthereum,
  arbitrum: NetworkArbitrumOne,
  optimism: NetworkOptimism,
  base: NetworkBase,
  polygon: NetworkPolygon,
  bsc: NetworkBinanceSmartChain,
  avalanche: NetworkAvalanche,
  solana: NetworkSolana,
  tron: NetworkTron,
};

/** Networks whose own mark is already a filled square: their plain logo, not on a background. */
const PLAIN_NETWORKS = new Set(["base"]);

/** CAIP-2 chain ids of the registry's networks, to their slug. */
const SLUG_BY_CHAIN: Record<string, string> = {
  "eip155:1": "ethereum",
  "eip155:11155111": "ethereum",
  "eip155:42161": "arbitrum",
  "eip155:421614": "arbitrum",
  "eip155:10": "optimism",
  "eip155:11155420": "optimism",
  "eip155:8453": "base",
  "eip155:84532": "base",
  "eip155:137": "polygon",
  "eip155:80002": "polygon",
  "eip155:56": "bsc",
  "eip155:97": "bsc",
  "eip155:43114": "avalanche",
  "eip155:43113": "avalanche",
  "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp": "solana",
  "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1": "solana",
  "tron:0x2b6653dc": "tron",
  "tron:0xcd8690dc": "tron",
};

/** Network display names as the API sends them, to their slug (for rows that carry only a name). */
const SLUG_BY_NAME: Record<string, string> = {
  ethereum: "ethereum",
  sepolia: "ethereum",
  "arbitrum one": "arbitrum",
  "arbitrum sepolia": "arbitrum",
  "op mainnet": "optimism",
  "op sepolia": "optimism",
  base: "base",
  "base sepolia": "base",
  polygon: "polygon",
  "polygon amoy": "polygon",
  "bnb smart chain": "bsc",
  "bnb smart chain testnet": "bsc",
  "avalanche c-chain": "avalanche",
  "avalanche fuji": "avalanche",
  solana: "solana",
  "solana devnet": "solana",
  tron: "tron",
  "tron nile": "tron",
};

function networkSlug(network: string): string {
  const key = network.trim().toLowerCase();
  return SLUG_BY_CHAIN[network] ?? SLUG_BY_NAME[key] ?? key;
}

/** Initials on a neutral badge, for anything without a logo. */
function Initials({ text, size, round }: { text: string; size: number; round: boolean }) {
  return (
    <span
      className={cx(
        "flex size-full items-center justify-center bg-subtle font-bold text-fg-muted",
        round ? "rounded-full" : "rounded-[28%]",
      )}
      style={{ fontSize: Math.max(8, Math.round(size * 0.3)) }}
    >
      {text.replace(/[^A-Za-z0-9]/g, "").slice(0, 3)}
    </span>
  );
}

/**
 * A token's logo, round. With `network`, the network's logo sits small on its corner, the way
 * wallets show "USDC on Base".
 */
export function TokenIcon({
  symbol,
  network,
  size = 24,
  className,
}: {
  symbol: string;
  /** Chain id (eip155:137), registry slug (polygon) or network name (Polygon). */
  network?: string;
  size?: number;
  className?: string;
}) {
  const Logo = TOKEN_LOGOS[symbol.toUpperCase()];
  // The network as a small round badge at the bottom right, a little outside the coin. A thin
  // transparent ring is cut out of the coin around it, so the two shapes stay apart at any size
  // and on any background (no border colour to match).
  const badge = Math.max(9, Math.round(size * 0.44));
  const overhang = Math.round(badge * 0.15);
  const gap = size >= 28 ? 2 : 1.5;
  const center = size + overhang - badge / 2;
  const notch = network
    ? `radial-gradient(circle at ${center}px ${center}px, transparent ${badge / 2 + gap}px, #000 ${badge / 2 + gap + 0.5}px)`
    : undefined;
  return (
    <span
      aria-hidden="true"
      className={cx("relative inline-flex shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <span
        className="flex size-full overflow-hidden rounded-full"
        style={{ mask: notch, WebkitMask: notch }}
      >
        {Logo ? <Logo variant="background" size={size} /> : <Initials text={symbol} size={size} round />}
      </span>
      {network && (
        <span className="absolute flex" style={{ right: -overhang, bottom: -overhang }}>
          <NetworkIcon network={network} size={badge} round />
        </span>
      )}
    </span>
  );
}

/** A network's logo, as a rounded square. */
export function NetworkIcon({
  network,
  size = 20,
  round = false,
  className,
}: {
  /** Chain id (eip155:137), registry slug (polygon) or network name (Polygon). */
  network: string;
  size?: number;
  /** A circle (the badge on a token) instead of a rounded square; every logo on its background. */
  round?: boolean;
  className?: string;
}) {
  const slug = networkSlug(network);
  const Logo = NETWORK_LOGOS[slug];
  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-flex shrink-0 overflow-hidden",
        round ? "rounded-full" : "rounded-[28%]",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {Logo ? (
        <Logo variant={PLAIN_NETWORKS.has(slug) && !round ? "branded" : "background"} size={size} />
      ) : (
        <Initials text={network} size={size} round={false} />
      )}
    </span>
  );
}
